<template>
  <div class="container py-5" dir="rtl">
    
    <!-- 1. قسم البحث (رقم الطلب) -->
    <div class="card shadow-sm mb-4 border-top-primary no-print">
      <div class="card-body p-4">
        <h4 class="mb-3 text-primary fw-bold">
          <i class="fas fa-search ms-2"></i> استعلام عن تظلم
        </h4>
        <form @submit.prevent="fetchAppealData" class="row g-3 align-items-center">
          <div class="col-md-6">
            <label class="visually-hidden">رقم الطلب</label>
            <div class="input-group">
              <span class="input-group-text bg-light"><i class="fas fa-hashtag"></i></span>
              <input 
                type="number" 
                class="form-control form-control-lg" 
                v-model="searchId" 
                placeholder="أدخل رقم طلب التظلم" 
                required
              >
              <button class="btn btn-primary px-4 fw-bold" type="submit" :disabled="loading">
                <span v-if="loading" class="spinner-border spinner-border-sm ms-2"></span>
                بحث
              </button>
            </div>
          </div>
          <div class="col-md-6 text-muted small">
            * أدخل رقم الإيصال أو رقم الطلب المسجل به التظلم.
          </div>
        </form>
      </div>
    </div>

    <!-- 2. قسم بيانات الطالب والمواد (يظهر بعد البحث) -->
    <div v-if="appealData" class="fade-in-up">
      
      <!-- كارت بيانات الطالب -->
      <div class="card shadow-sm mb-4 bg-light border-0">
        <div class="card-body">
          <div class="row align-items-center">
            <div class="col-md-8">
              <h5 class="fw-bold text-dark mb-1">الطالب: {{ appealData.student_name }}</h5>
              <p class="mb-0 text-muted">
                رقم الجلوس: <strong class="text-dark">{{ appealData.seating_no }}</strong> | 
                تاريخ الطلب: {{ appealData.date }}
              </p>
            </div>
            <div class="col-md-4 text-end">
              <span class="badge bg-success fs-6 px-3 py-2">حالة الطلب: {{ appealData.status_text }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- شبكة المواد -->
      <h5 class="fw-bold mb-3 border-bottom pb-2">المواد المتظلم فيها:</h5>
      <div class="row g-3 mb-5">
        <div v-for="subject in appealData.subjects" :key="subject.id" class="col-md-4 col-lg-3">
          
          <div 
            class="card h-100 text-center subject-card"
            :class="{'active-subject': selectedSubject?.id === subject.id}"
            @click="loadPaper(subject)"
          >
            <div class="card-body d-flex flex-column justify-content-center align-items-center">
              
              <!-- أيقونة الحالة -->
              <div class="mb-3">
                <i v-if="subject.paper_status === 'ready'" class="fas fa-book-open fa-3x text-primary"></i>
                <i v-else class="fas fa-clock fa-3x text-secondary opacity-50"></i>
              </div>
              
              <h5 class="fw-bold mb-1">{{ subject.subject_name }}</h5>
              
              <!-- حالة الورقة -->
              <div class="mt-2">
                <span v-if="subject.paper_status === 'ready'" class="badge bg-success">
                  جاهزة للعرض
                </span>
                <span v-else class="badge bg-secondary">
                  جاري التجهيز
                </span>
              </div>

            </div>
            
            <!-- زر (يظهر فقط إذا كانت جاهزة) -->
            <div class="card-footer bg-white border-0 pb-3" v-if="subject.paper_status === 'ready'">
               <small class="text-primary fw-bold">اضغط للمشاهدة <i class="fas fa-arrow-down"></i></small>
            </div>
          </div>

        </div>
      </div>

    </div>

    <!-- 3. قسم العرض (الفليب بوك) -->
    <!-- يظهر فقط عند اختيار مادة وتكون الصور محملة -->
    <div v-if="selectedSubjectImages.length > 0" class="mt-4" ref="viewerSection">
        <div class="alert alert-info d-flex justify-content-between align-items-center">
            <span>
                <i class="fas fa-eye ms-2"></i> 
                جاري عرض ورقة مادة: <strong>{{ selectedSubject.subject_name }}</strong>
            </span>
            <button @click="closeViewer" class="btn btn-sm btn-outline-danger">إغلاق المعاينة</button>
        </div>

        <!-- استدعاء المكون الابن -->
        <ExamPaperViewer 
            :images="selectedSubjectImages"
            :studentName="appealData.student_name"
            :subjectName="selectedSubject.subject_name"
        />
    </div>

    <!-- رسالة خطأ -->
    <div v-if="error" class="alert alert-danger mt-3 text-center">
      <i class="fas fa-exclamation-triangle ms-2"></i> {{ error }}
    </div>

  </div>
</template>

<script>
import api from '@/api/axios';
import ExamPaperViewer from './ExamPaperViewer.vue';

export default {
  name: "AppealReviewPage",
  components: { ExamPaperViewer },
  data() {
    return {
      searchId: '',
      loading: false,
      error: null,
      appealData: null, // بيانات الطلب كاملة
      
      selectedSubject: null, // المادة المختارة حالياً
      selectedSubjectImages: [] // صور المادة المختارة
    };
  },
  methods: {
    // 1. البحث عن الطلب
    async fetchAppealData() {
      this.loading = true;
      this.error = null;
      this.appealData = null;
      this.selectedSubjectImages = []; // تصفير القديم

      try {
        const res = await api.get(`/appeals/${this.searchId}/details`);
        this.appealData = res.data;
      } catch (err) {
        this.error = "لم يتم العثور على طلب بهذا الرقم، أو حدث خطأ في النظام.";
      } finally {
        this.loading = false;
      }
    },

    // 2. اختيار مادة وعرض صورها
    async loadPaper(subject) {
      if(subject.paper_status !== 'ready') {
          alert("عفواً، صورة ورقة الإجابة لهذه المادة لم يتم رفعها من الكنترول بعد.");
          return;
      }

      this.selectedSubject = subject;
      this.selectedSubjectImages = []; // تصفير لحظي لمنع التداخل

      try {
        // جلب الصور الخاصة بهذه المادة
        // الـ Endpoint المقترحة في الباك اند: /api/appeals/subjects/{id}/images
        const res = await api.get(`/appeals/subjects/${subject.id}/images`);
        
        this.selectedSubjectImages = res.data.images; // مصفوفة روابط الصور

        // تمرير الشاشة للأسفل تلقائياً ليرى المستخدم الورقة
        this.$nextTick(() => {
            this.$refs.viewerSection.scrollIntoView({ behavior: 'smooth' });
        });

      } catch (err) {
        alert("حدث خطأ أثناء تحميل الصور.");
      }
    },

    closeViewer() {
        this.selectedSubjectImages = [];
        this.selectedSubject = null;
    }
  }
};
</script>

<style scoped>
.border-top-primary { border-top: 4px solid #0d6efd; }

.subject-card {
    cursor: pointer;
    transition: all 0.3s ease;
    border: 2px solid transparent;
}

.subject-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0,0,0,0.1);
}

/* تمييز المادة النشطة */
.active-subject {
    border-color: #0d6efd;
    background-color: #f0f8ff;
}

.fade-in-up {
    animation: fadeInUp 0.5s ease-out;
}

@keyframes fadeInUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
}
</style>